import React from 'react';
import {useSelector} from 'react-redux';

import AemComponent from '@/components/Aem/AemComponent/AemComponent';
import EditableText from '@/components/Aem/EditableText/EditableText';
import {AppButton} from '@/components/Button';
import DeviceIcon from '@/components/DeviceIcon/DeviceIcon';
import {StickyHeader} from '@/components/Layout';
import appConfig from '@/config/appConfig.typed';
import {
    AD_SELECT_ISSUE_HEADER_TITLE,
    ADVANCED_DIAGNOSTICS_NOT_NOW_BUTTON_ARIA_LABEL,
    ADVANCED_DIAGNOSTICS_NOT_NOW_BUTTON_TEXT,
    ADVANCED_DIAGNOSTICS_SELECT_ISSUE_BUTTON_ARIA_LABEL,
} from '@/consts/localization/localizationKeys';
import routePaths from '@/consts/route/routePaths';
import useIsMobileLayout from '@/hooks/layout/useIsMobileLayout';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import {getAriaLabel} from '@/services/accessibilityService';
import amplitudeADTracking from '@/services/analytics/amplitude/amplitudeADTracking';
import googleAnalyticsService from '@/services/analytics/ga/googleAnalyticsService';
import {getLocalizedStrings} from '@/services/localization/localizationService';
import routerService from '@/services/route/routerService';
import {selectDeviceType} from '@/state/selectors/device';

import AdvancedDiagnosticDescription from '../components/AdvancedDiagnosticDescription';
import AdvancedDiagnosticLayout from '../components/AdvancedDiagnosticLayout';
import AdvancedDiagnosticTitle from '../components/AdvancedDiagnosticTitle';
import NotNowButtonAD from '../components/NotNowButtonAD';
import useRedirectToPhase2 from '../hooks/useRedirectToPhase2';
import AdvancedDiagnosticIssueList from './components/AdvancedDiagnosticIssueList';

const AdvancedDiagnosticSelectIssue = () => {
    const isMobile = useIsMobileLayout();
    const deviceType = useSelector(selectDeviceType);
    const diagnosticData = appConfig.getAdConfig(deviceType, 'step2');
    const redirectToPhase2 = useRedirectToPhase2();
    const localizedStrings = getLocalizedStrings();

    const onNextButtonClick = async () => {
        amplitudeADTracking.trackADNoneOfTheAboveClick();
        googleAnalyticsService.trackAdNoneOfTheAboveClick();
        redirectToPhase2();
    };

    useDocumentTitle(localizedStrings[AD_SELECT_ISSUE_HEADER_TITLE]);

    return (
        <AdvancedDiagnosticLayout>
            <AdvancedDiagnosticLayout.ContentWrapper>
                <AdvancedDiagnosticLayout.Container
                    sectionClassName='justify-between'
                    onBackButtonClick={isMobile && routerService.goBack}
                >
                    {isMobile && (
                        <StickyHeader
                            onBack={routerService.goBack}
                            title={localizedStrings[AD_SELECT_ISSUE_HEADER_TITLE]}
                        />
                    )}
                    <div className='flex h-full w-full flex-col items-center'>
                        <DeviceIcon mode='diagnostics' className='h-16 w-16' />
                        <AdvancedDiagnosticTitle itemName='selectissuetitle' />
                        <AdvancedDiagnosticDescription itemName='selectissuedesc' />
                        <AdvancedDiagnosticIssueList issuesData={diagnosticData} />
                    </div>
                    <div className='mx-auto mt-8 flex w-full flex-col items-center gap-4 ml:gap-3'>
                        <AppButton
                            secondary={isMobile}
                            primary={!isMobile}
                            light
                            onClick={onNextButtonClick}
                            className='m-auto ml:w-full'
                            aria-label={getAriaLabel(ADVANCED_DIAGNOSTICS_SELECT_ISSUE_BUTTON_ARIA_LABEL)}
                        >
                            <AemComponent
                                component={EditableText}
                                pageName={routePaths.ADVANCE_DIAGNOSTIC}
                                itemName='selectissuebutton'
                            />
                        </AppButton>
                        {!isMobile && (
                            <NotNowButtonAD
                                className='ml:w-full'
                                aria-label={getAriaLabel(ADVANCED_DIAGNOSTICS_NOT_NOW_BUTTON_ARIA_LABEL)}
                            >
                                {localizedStrings[ADVANCED_DIAGNOSTICS_NOT_NOW_BUTTON_TEXT]}
                            </NotNowButtonAD>
                        )}
                    </div>
                </AdvancedDiagnosticLayout.Container>
            </AdvancedDiagnosticLayout.ContentWrapper>
        </AdvancedDiagnosticLayout>
    );
};

export default AdvancedDiagnosticSelectIssue;
